import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight,
  FileText,
  Settings,
  Calculator,
  Send,
  Users,
  ClipboardCheck,
  GitBranch,
  Activity,
  BarChart3,
  Brain,
  Link2,
  Lock,
  CheckCircle2
} from 'lucide-react';

const modules = [
  {
    icon: FileText,
    title: 'RFQ Capture & Tracking',
    description: 'Centralized workspace for all customer RFQs with live status, SLA timers, and automated escalations.',
    features: [
      'Centralized RFQ inbox with auto-categorization',
      'Real-time status tracking and SLA monitoring',
      'Automated alerts and escalation triggers',
      'Document standardization and attachment management',
    ],
    color: '#00D4FF',
  },
  {
    icon: Settings,
    title: 'BOM & Technical Evaluation',
    description: 'Structured BOM management with version control, revision tracking, and technical feasibility assessment.',
    features: [
      'Hierarchical BOM structure management',
      'Automatic version control and comparison',
      'Technical feasibility documentation',
      'Drawing and specification linking',
    ],
    color: '#3B82F6',
  },
  {
    icon: Calculator,
    title: 'Smart Quote Engine',
    description: 'AI-powered costing with norms libraries, material pricing, and automated quote generation.',
    features: [
      'Norms-based automatic costing',
      'Material and process cost libraries',
      'Multi-level costing breakdowns',
      'What-if scenario analysis',
    ],
    color: '#8B5CF6',
  },
  {
    icon: Send,
    title: 'Customer Quote',
    description: 'Professional quotation generation with customizable templates and pricing structures.',
    features: [
      'Customizable quote templates',
      'Dynamic pricing adjustments',
      'Quote comparison and versioning',
      'Customer-specific formatting',
    ],
    color: '#F59E0B',
  },
  {
    icon: Users,
    title: 'Vendor Quote',
    description: 'Streamlined supplier RFQ distribution with automated response collection and comparison.',
    features: [
      'Supplier RFQ distribution',
      'Automated response collection',
      'Quote comparison matrix',
      'Negotiation tracking',
    ],
    color: '#EC4899',
  },
  {
    icon: ClipboardCheck,
    title: 'APQP Management',
    description: 'Complete APQP governance with milestone tracking, PPAP documentation, and SOP readiness.',
    features: [
      'PPAP document management',
      'Milestone and timeline tracking',
      'SOP readiness dashboard',
      'Customer audit preparation',
    ],
    color: '#14B8A6',
  },
  {
    icon: GitBranch,
    title: 'Workflow & Approvals',
    description: 'Configurable multi-level approval workflows with role-based routing and escalations.',
    features: [
      'Custom workflow builder',
      'Role-based approval routing',
      'Automatic escalation rules',
      'Mobile approval support',
    ],
    color: '#00D4FF',
  },
  {
    icon: Activity,
    title: 'Audit Trail',
    description: 'Complete traceability with change history, user actions, and compliance documentation.',
    features: [
      'Complete change history',
      'User action logging',
      'Document version tracking',
      'Compliance reporting',
    ],
    color: '#3B82F6',
  },
  {
    icon: BarChart3,
    title: 'Dashboards & Analytics',
    description: 'Real-time business intelligence with KPIs, performance metrics, and management reports.',
    features: [
      'Real-time KPI dashboards',
      'Performance analytics',
      'Custom report builder',
      'Executive summaries',
    ],
    color: '#8B5CF6',
  },
  {
    icon: Brain,
    title: 'AI Capabilities',
    description: 'Intelligent automation with AI-powered BOM extraction, analytics, and decision support.',
    features: [
      'AI-powered BOM extraction',
      'Predictive analytics',
      'Smart recommendations',
      'Pattern recognition',
    ],
    color: '#F59E0B',
  },
  {
    icon: Link2,
    title: 'ERP Integration',
    description: 'Seamless connectivity with SAP, Oracle, and other enterprise systems.',
    features: [
      'SAP integration',
      'Oracle connectivity',
      'API-first architecture',
      'Data synchronization',
    ],
    color: '#EC4899',
  },
  {
    icon: Lock,
    title: 'Security & Access',
    description: 'Enterprise-grade security with role-based access control and data encryption.',
    features: [
      'Role-based access control',
      'Data encryption at rest and transit',
      'SSO/SAML support',
      'Audit compliance',
    ],
    color: '#14B8A6',
  },
];

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const ProductPage = () => {
  return (
    <div className="pt-24" data-testid="product-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-sm font-medium">
              The Platform
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Complete RFQ-to-SOP Platform
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              One connected platform covering the entire quotation lifecycle. From RFQ capture 
              to production-ready SOPs, every step is digitized and optimized.
            </p>

            {/* Workflow Visual */}
            <div className="glass-card p-6 md:p-8 max-w-4xl mx-auto overflow-x-auto">
              <div className="flex items-center justify-between min-w-[600px] gap-4">
                {['RFQ', 'BOM', 'Costing', 'Quote', 'Vendor Quote', 'APQP', 'SOP'].map((stage, index) => (
                  <div key={stage} className="flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center font-semibold text-sm ${
                        index === 0 ? 'bg-[#00D4FF]/20 text-[#00D4FF] border border-[#00D4FF]/30' :
                        index === 6 ? 'bg-[#00D4FF] text-black' :
                        'bg-white/5 text-white/70 border border-white/10'
                      }`}>
                        {stage}
                      </div>
                    </div>
                    {index < 6 && (
                      <div className="w-8 h-0.5 bg-gradient-to-r from-[#00D4FF]/50 to-white/10" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-24" data-testid="modules-section">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">Platform Modules</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
              Comprehensive Feature Set
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Everything you need to digitize and optimize your quotation operations
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <AnimatedSection key={module.title}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="glass-card p-6 h-full group"
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors"
                    style={{ backgroundColor: `${module.color}15` }}
                  >
                    <module.icon className="w-6 h-6" style={{ color: module.color }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                  <p className="text-white/60 text-sm mb-4">{module.description}</p>
                  <ul className="space-y-2">
                    {module.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/50">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: module.color }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  See the Platform in Action
                </h2>
                <p className="text-white/60 mb-8 max-w-xl mx-auto">
                  Book a personalized demo to see how Cost Genie can transform your quotation operations.
                </p>
                <Link
                  to="/demo"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all"
                  data-testid="product-demo-btn"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default ProductPage;
